import { installGoogleTranslateResiliencePatch } from '~/utils/installGoogleTranslateResiliencePatch';

const TRANSLATED_CLASS_PREFIX = 'translated-';

const isMarkedAsTranslated = (root: Element) =>
  Array.from(root.classList).some((className) => className.startsWith(TRANSLATED_CLASS_PREFIX));

/**
 * Installs installGoogleTranslateResiliencePatch (see PLA-1259) only once the browser's
 * translator has actually started translating the page, instead of unconditionally at boot.
 *
 * Chrome signals that it's translating a page by adding a `translated-ltr`/`translated-rtl`
 * class (and flipping `lang`) on the root `<html>` element, a few hundred ms before it touches
 * any text. This watches for that signal with a `MutationObserver` limited to the `class`/`lang`
 * attributes of the root element - effectively free, since attribute changes on the root
 * element are extremely rare outside of translation - and only then installs the actual
 * `removeChild`/`insertBefore` patch. For the overwhelming majority of sessions where
 * translation never happens, `removeChild`/`insertBefore` stay fully native: no patch is ever
 * installed, so there's no "little bit slower" cost at all (see
 * https://github.com/facebook/react/issues/11538#issuecomment-417504600 for that trade-off on
 * the unconditional version).
 *
 * Trade-off: this detection is specific to Chrome/Chromium's translate signal. A hypothetical
 * translator that mutates the DOM without flipping that class/lang combination on `<html>`
 * first would never trigger the patch - `installGoogleTranslateResiliencePatch()` on its own
 * protects against any DOM-mutating translator, unconditionally; this lazy wrapper only
 * protects against ones we can detect first, which matches what we've actually observed for
 * this crash (Chrome Mobile iOS - see PLA-1259).
 */
export const installGoogleTranslateResiliencePatchLazily = (
  root: Element | null = typeof document !== 'undefined' ? document.documentElement : null,
) => {
  if (!root) {
    return;
  }

  if (isMarkedAsTranslated(root)) {
    installGoogleTranslateResiliencePatch();
    return;
  }

  if (typeof MutationObserver !== 'function') {
    // No way to detect the translation signal lazily - fall back to the unconditional patch.
    installGoogleTranslateResiliencePatch();
    return;
  }

  const observer = new MutationObserver(() => {
    if (isMarkedAsTranslated(root)) {
      installGoogleTranslateResiliencePatch();
      observer.disconnect();
    }
  });

  observer.observe(root, { attributes: true, attributeFilter: ['class', 'lang'] });
};
